import { ButtonBase } from '@mui/material';
import Grid from '@mui/material/Grid';
import Cookies from 'js-cookie';
import { Fragment, useState } from 'react';
import Eyebrow from '@/components/Eyebrow/Eyebrow';
import LocationDisplay from '@/components/GlobalNavigation/Location/LocationDisplay/LocationDisplay';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import Link from '@/components/Link/Link';
import { getLoginUrlByEnv, MYSUNRUN_URL } from '@/constants/baseUrls';
import {
  ContentStyle,
  type GlobalFooterContent,
} from '@/contentful/contentful-exports';
import { Environment } from '@/utils/environment';
import useStyles from './GlobalFooter.styles';
import SolarByState from './SolarByState/SolarByState';

interface GlobalFooterProps {
  content: GlobalFooterContent;
}

export default function GlobalFooter({
  content: {
    fields: {
      style,
      t1Menu,
      linkColumns,
      solarByStateMenu,
      socialIconLinks,
      copyrightText,
      legalLinks,
    },
  },
}: GlobalFooterProps) {
  const isOnDark = style === ContentStyle.OnDark;
  const [solarByStateOpen, setSolarByStateOpen] = useState<boolean>(false);
  const { classes, cx } = useStyles({ isOnDark });

  // biome-ignore lint/correctness/noNestedComponentDefinitions: closes over per-render props
  // biome-ignore lint/correctness/noComponentHookFactories: closes over per-render props
  const Socials = () => (
    <div className={classes.socialIcons}>
      {socialIconLinks.map(
        ({
          fields: {
            url,
            icon: {
              fields: { name },
            },
          },
        }) => (
          <Link
            href={url}
            key={name}
            className={classes.linkColor}
            aria-label={
              name ? `Footer ${name} link` : 'Footer Social Media link'
            }
            noPrefetchOnHover
          >
            <Icon name={name} />
          </Link>
        )
      )}
    </div>
  );

  const clearConsentCookiesAndReload = () => {
    // Clear all cookies related to consent
    const consentCookies = [
      'sunrun_allow_analytics_storage',
      'sunrun_allow_add_storage',
      'sunrun_cookie_set',
    ];

    consentCookies.forEach((cookieName) => {
      Cookies.remove(cookieName);
    });

    // Reload the page to trigger the cookie consent banner
    window.location.reload();
  };

  return (
    <footer className={classes.container}>
      <GridContainer fixed>
        {/* T1 */}
        <Grid
          className={classes.t1}
          size={{
            xs: 12,
            md: 3,
          }}
        >
          {t1Menu.fields.links?.map(({ fields: { text, url } }) => (
            <Link
              className={cx(classes.t1Row, classes.linkColor)}
              key={text}
              href={url}
              noPrefetchOnHover
            >
              <div className={classes.t1Item}>
                <p>{text}</p>
                {!url.startsWith('/') && (
                  <Icon
                    name={IconName.EXTERNAL_LINK}
                    className={classes.externalLinkIcon}
                  />
                )}
              </div>
              <Icon
                name={IconName.ARROW_RIGHT}
                className={classes.t1HoverIcon}
              />
            </Link>
          ))}
        </Grid>

        {/* Link Columns */}
        {linkColumns.map(({ fields: { name, links } }, index) => (
          <Grid
            key={name}
            size={{
              xs: 12,
              sm: 4,
              md: 2,
            }}
          >
            <div
              className={cx(
                classes.linkColumn,
                index === 0 && classes.firstLinkColumn
              )}
            >
              <Eyebrow
                text={name || ''}
                isOnDark={isOnDark}
                className='linkColumnEyebrow'
              />
              {links?.map(({ fields: { text, url } }) => {
                const verifiedUrl =
                  url === MYSUNRUN_URL[Environment.PROD]
                    ? getLoginUrlByEnv()
                    : url;
                return (
                  <div
                    className={cx(classes.linkColumnItem, classes.linkColor)}
                    key={text}
                  >
                    <Link href={verifiedUrl} noPrefetchOnHover>
                      {text}
                    </Link>
                  </div>
                );
              })}
              {index === 0 && (
                <SolarByState
                  isOnDark={isOnDark}
                  solarByStateMenu={solarByStateMenu}
                  solarByStateOpen={solarByStateOpen}
                  setSolarByStateOpen={setSolarByStateOpen}
                />
              )}
            </div>
          </Grid>
        ))}

        {/* Socials Md+ */}
        <Grid
          className={classes.socialIconsLargeContainer}
          size={{
            md: 3,
          }}
        >
          <Socials />
        </Grid>
      </GridContainer>
      {/* Lower Content */}
      <GridContainer fixed gridProps={{ className: classes.lowerContent }}>
        <Grid size={12}>
          <div
            className={cx('be-ix-link-block', classes.popularPagesList)}
          ></div>
        </Grid>
        <Grid className={classes.primaryLowerContent} size={12}>
          <div className={classes.stateAndCopy}>
            <LocationDisplay
              className={classes.stateSelector}
              isOnDark={isOnDark}
            />
            <div className={classes.copyright}>{copyrightText}</div>
          </div>
          <div className={classes.lowerContentSocials}>
            <Socials />
          </div>
          <div className={classes.legalLinks}>
            {/* Map through all links except the last one */}
            {legalLinks.fields.links
              ?.slice(0, -1)
              .map(({ fields: { text, url } }) => (
                <Fragment key={text}>
                  <Link
                    href={url}
                    className={classes.legalLink}
                    noPrefetchOnHover
                  >
                    {text}
                  </Link>
                  <span className={classes.legalLinksSlash}>/</span>
                </Fragment>
              ))}

            {/* Manage Cookies link at n-1 position */}
            <ButtonBase
              disableRipple
              type='button'
              className={cx(classes.legalLink, 'manageCookieLink')}
              onClick={clearConsentCookiesAndReload}
            >
              Manage Cookies
            </ButtonBase>
            <span className={classes.legalLinksSlash}>/</span>

            {/* Render the last legal link */}
            {legalLinks.fields.links
              ?.slice(-1)
              .map(({ fields: { text, url } }) => (
                <Fragment key={text}>
                  <Link
                    href={url}
                    className={classes.legalLink}
                    noPrefetchOnHover
                  >
                    {text}
                  </Link>
                </Fragment>
              ))}

            <span className={classes.legalLinksSlash}>/</span>
            <Link
              href='/llms.txt'
              className={classes.legalLink}
              noPrefetchOnHover
            >
              LLMs.txt
            </Link>
            <span className={classes.legalLinksSlash}>/</span>
            <Link
              href='/ai.txt'
              className={classes.legalLink}
              noPrefetchOnHover
            >
              AI.txt
            </Link>

            <div className={classes.privacyOptions}>
              <Icon name={IconName.PRIVACY_OPTIONS} width={30} />
            </div>
          </div>
        </Grid>
      </GridContainer>
    </footer>
  );
}
